import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { LgvGridManager } from '../../grid-manager/grid-manager';
import { LoanAppPageService } from '../../../core/services/page-services/loan-app-page.service';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import { LgvLoanApp } from '../../../data-models/lgv-loan-application.model';
import { DocumentViewerModalComponent, PagePurpose } from '../../document-viewer-modal/document-viewer-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { LgvAuthMgrService } from '../../../core/services/lgv-auth-mgr.service';
import { LoanAppVaultModalComponent } from '../../loan-app-vault-modal/loan-app-vault-modal.component';
import { NotificationService } from '../../../core/core.module';

@Component({
    selector: 'lgv-loan-app-vault',
    templateUrl: './loan-app-vault.component.html',
    styleUrls: ['./loan-app-vault.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoanAppVaultComponent implements OnInit, OnDestroy {

    vaultDocuments$ = new BehaviorSubject<LgvLoanApp.VaultDocument[]>([]);
    allVaultDocuments: LgvLoanApp.VaultDocument[] = [];
    loanAppId: string;
    GridMgr: LgvGridManager;
    isLoading$ = new BehaviorSubject<boolean>(false);
    onDestroy$ = new Subject<void>();
    currentPage = 1;
    countPerPage = 25;
    totalCount = 0;
    searchText = '';
    uploadBoxCSS = '';
    filesToUpload: File[] = null;
    get isClient(): boolean { return this.lgvAuthMgrSvc.IsClient; }
    get isInternalUser(): boolean { return !this.isClient; }

    constructor(
        private lgvLoanAppPageService: LoanAppPageService,
        private lgvLoanService: LgvLoanApplicationService,
        private matDialog: MatDialog,
        public lgvAuthMgrSvc: LgvAuthMgrService,
        private notificationSvc: NotificationService,
    ) { }

    ngOnInit(): void {
        this.isLoading$.next(true);
        this.GridMgr = new LgvGridManager();
        this.GridMgr.Init('documentName', 'asc');
        this.loanAppId = this.lgvLoanAppPageService.selectedLoanApplication$.value.id;
        
        this.fetchVaultDocuments();

        this.GridMgr.OnRefreshData.pipe(takeUntil(this.onDestroy$)).subscribe(
            (refreshData) => {
                let sortedArray = [];
                if (refreshData?.data?.direction === 'asc') {
                    sortedArray = this.allVaultDocuments.sort((a, b) =>
                        a[refreshData?.data?.column] > b[refreshData?.data?.column] ? 1 : -1
                    );
                } else {
                    sortedArray = this.allVaultDocuments.sort((a, b) =>
                        a[refreshData?.data?.column] < b[refreshData?.data?.column] ? 1 : -1
                    );
                }
                this.vaultDocuments$.next(sortedArray);
                this.isLoading$.next(false);
            }
        );
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    fetchVaultDocuments(searchText: string = ''): void {
        this.isLoading$.next(true);

        this.lgvLoanService.fetchVaultDocuments(this.loanAppId).pipe(takeUntil(this.onDestroy$)).subscribe(
            (vaultDocuments) => {
                if (searchText) {
                    this.allVaultDocuments = vaultDocuments.filter(doc =>
                        doc.name.toLowerCase().includes(searchText.toLowerCase())
                    );
                } else {
                    this.allVaultDocuments = vaultDocuments;
                }

                this.totalCount = this.allVaultDocuments.length;
                this.updatePageData();
                this.isLoading$.next(false);
            },
            error => {
                console.error('Error fetching vault documents:', error);
                this.isLoading$.next(false);
            }
        );
    }

    onAddVaultDocument(mode: string = 'addVaultDocument'): void {
        const dialogRef = this.matDialog.open(LoanAppVaultModalComponent, {
            width: '600px',
            data: {
                loanAppId: this.loanAppId,
                mode: mode,
                selectedFiles: this.filesToUpload
            }
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(result => {
            if (result?.action) {
                this.filesToUpload = null;
                this.notificationSvc.success('Vault document has been added.');
                this.updatePageData();
                this.fetchVaultDocuments();
            }
        });
    }

    onDragOver(event) {
        event.preventDefault();
        //console.log('onDragOver...');
        if (this.containsFiles(event)) {
            this.uploadBoxCSS = 'file-hover';
        }
    }

    onDragLeave(event) {
        event.preventDefault();
        //console.log('onDragLeave...');
        this.uploadBoxCSS = '';
    }

    onDropSuccess(event) {
        //console.log('onDropSuccess()');
        //console.log(event);
        event.preventDefault();
        this.uploadBoxCSS = '';

        this.onFileChange(event.dataTransfer.files);
    }

    onClickEvent(event) {
        this.onAddVaultDocument('addVaultDocument');
    }

    // From attachment link
    onChange(event) {
        console.log('onChange()');
        this.onFileChange(event.target.files);
    }

    containsFiles(event) {
        if (event.dataTransfer.types) {
            for (var i = 0; i < event.dataTransfer.types.length; i++) {
                if (event.dataTransfer.types[i] == "Files") {
                    return true;
                }
            }
        }
        return false;
    }

    private onFileChange(files: File[]) {
        console.log('onFilesChanged');
        console.log(files);
        this.filesToUpload = files;
        this.onAddVaultDocument('addVaultDocument');
    }

    onSearch(): void {
        this.fetchVaultDocuments(this.searchText);
    }

    updatePageData(): void {
        const start = (this.currentPage - 1) * this.countPerPage;
        const end = start + this.countPerPage;
        const pageData = this.allVaultDocuments.slice(start, end);
        this.vaultDocuments$.next(pageData);
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.updatePageData();
        }
    }

    nextPage(): void {
        if (this.currentPage * this.countPerPage < this.totalCount) {
            this.currentPage++;
            this.updatePageData();
        }
    }

    openVaultDocument(document: LgvLoanApp.VaultDocument): void {
        this.matDialog.open(DocumentViewerModalComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '95%',
            width: '95%',
            data: {
                applicationId: this.loanAppId,
                vaultDocument: document,
                pagePurpose: PagePurpose.VaultDocument,
                newWindow: false
            }
        }).afterClosed().subscribe((result) => {
            if (result?.action) {
                this.fetchVaultDocuments();
            }
        });
    }

    deleteDocument(documentId: string): void {
        const loanAppId = this.lgvLoanAppPageService.loanAppId$.value;
        this.lgvLoanService.deleteVaultDocument(loanAppId, documentId).pipe(takeUntil(this.onDestroy$)).subscribe(
            () => {
                // Remove the deleted document from the list
                this.allVaultDocuments = this.allVaultDocuments.filter(doc => doc.documentId !== documentId);
                this.totalCount = this.allVaultDocuments.length;
                this.fetchVaultDocuments();
                this.updatePageData();
                this.notificationSvc.success('Vault document has been deleted.');
            },
            error => {
                console.error('Error deleting vault document:', error);
                this.notificationSvc.error('There was an error deleting the vault document.');
            }
        );
    }
}
